import React, { useState } from 'react';
import { DB } from '../data/database';
import { exportCSV } from '../data/constants';
import { useLanguage } from '../translations/LanguageContext';
import { SearchIcon, EditIcon, CheckIcon } from './Icons';
import Pagination from './ui/Pagination';
import updateUnitStatus from '../utils/updateUnitStatus';

const timeAgo = (d) => {
  if (!d) return '';
  const ms = Date.now() - new Date(d).getTime();
  if (ms < 60000) return 'just now';
  if (ms < 3600000) return Math.floor(ms / 60000) + ' min ago';
  if (ms < 86400000) return Math.floor(ms / 3600000) + 'h ago';
  if (ms < 172800000) return 'yesterday';
  return Math.floor(ms / 86400000) + 'd ago';
};

const WS_STATUSES = [
  { id: 'live', label: 'Live on site', color: '#22C55E', dot: '●' },
  { id: 'reserved', label: 'Reserved', color: '#F59E0B', dot: '●' },
  { id: 'arriving', label: 'Arriving', color: '#3B82F6', dot: '●' },
  { id: 'sold', label: 'Sold', color: '#6B7280', dot: '●' },
];

function getAllUnits() {
  return [
    ...(DB.get('vehicles') || []).map(u => ({ ...u, _key: 'vehicles', _type: 'vehicle' })),
    ...(DB.get('engines') || []).map(u => ({ ...u, _key: 'engines', _type: 'engine' })),
    ...(DB.get('spareParts') || []).map(u => ({ ...u, _key: 'spareParts', _type: 'part' })),
  ];
}

function getName(u) {
  return u.make ? `${u.make} ${u.model || ''}`.trim() : u.engineName || u.partName || 'Unit';
}

function getSpecs(u) {
  return [u.year, u.color].filter(Boolean).join(' · ');
}

const MAX_FEATURED = 6;

function WebsiteCars({ user }) {
  const { t } = useLanguage();
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [editUnit, setEditUnit] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [menuOpen, setMenuOpen] = useState(null);
  const [imgPreview, setImgPreview] = useState(null);
  const [page, setPage] = useState(1);
  const [, forceUpdate] = useState();
  const perPage = 15;

  const allUnits = getAllUnits();
  const customers = DB.get('customers') || [];

  // Auto-sync: if internal status is sold, set website_status to sold
  allUnits.forEach(u => {
    if (['sold', 'sold_awaiting_payment', 'shipped', 'delivered'].includes(u.status) && u.show_on_website && (u.website_status || 'live') !== 'sold') {
      const list = DB.get(u._key) || [];
      DB.set(u._key, list.map(i => i.id === u.id ? { ...i, website_status: 'sold' } : i));
      u.website_status = 'sold';
    }
  });

  const websiteUnits = allUnits.filter(u => u.show_on_website || u.website_status === 'hidden');

  // Tab filtering
  let filtered = websiteUnits;
  if (tab === 'live') filtered = filtered.filter(u => u.show_on_website && (u.website_status || 'live') === 'live');
  else if (tab === 'reserved') filtered = filtered.filter(u => u.show_on_website && u.website_status === 'reserved');
  else if (tab === 'arriving') filtered = filtered.filter(u => u.show_on_website && u.website_status === 'arriving');
  else if (tab === 'sold') filtered = filtered.filter(u => u.show_on_website && u.website_status === 'sold');
  else if (tab === 'hidden') filtered = filtered.filter(u => !u.show_on_website);
  else filtered = filtered.filter(u => u.show_on_website); // 'all' = on website

  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter(u => [u.make, u.model, u.chassis, u.sku, u.engineName, u.partName, u.color].filter(Boolean).join(' ').toLowerCase().includes(q));
  }

  // Sort: featured first, then by website_sort_order, then updated
  filtered.sort((a, b) => {
    if (a.is_featured && !b.is_featured) return -1;
    if (!a.is_featured && b.is_featured) return 1;
    if ((a.website_sort_order || 999) !== (b.website_sort_order || 999)) return (a.website_sort_order || 999) - (b.website_sort_order || 999);
    return (b.website_updated_at || b.updatedAt || b.createdAt || '').localeCompare(a.website_updated_at || a.updatedAt || a.createdAt || '');
  });

  // Counts
  const onWeb = allUnits.filter(u => u.show_on_website);
  const counts = {
    all: onWeb.length,
    live: onWeb.filter(u => (u.website_status || 'live') === 'live').length,
    reserved: onWeb.filter(u => u.website_status === 'reserved').length,
    arriving: onWeb.filter(u => u.website_status === 'arriving').length,
    sold: onWeb.filter(u => u.website_status === 'sold').length,
    hidden: allUnits.filter(u => !u.show_on_website && (u.website_status === 'hidden' || websiteUnits.some(w => w.id === u.id))).length,
  };
  // Count not-on-website for import button
  const importableCount = allUnits.filter(u => !u.show_on_website).length;

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * perPage, safePage * perPage);

  // Helpers
  const saveUnit = (unit, updates) => {
    const list = DB.get(unit._key) || [];
    DB.set(unit._key, list.map(i => i.id === unit.id ? { ...i, ...updates, website_updated_at: new Date().toISOString(), website_updated_by: user.name } : i));
    forceUpdate({});
  };

  const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const selectAllPage = () => setSelected(prev => prev.size === paged.length && paged.length > 0 ? new Set() : new Set(paged.map(u => u.id)));

  const bulkAction = (action) => {
    const units = allUnits.filter(u => selected.has(u.id));
    units.forEach(u => {
      if (action === 'hide') saveUnit(u, { show_on_website: false, website_status: 'hidden', is_featured: false });
      else if (action === 'feature') {
        const currentFeatured = onWeb.filter(x => x.is_featured).length;
        if (currentFeatured + units.length > MAX_FEATURED) { alert(`Maximum ${MAX_FEATURED} featured units.`); return; }
        saveUnit(u, { is_featured: true, show_on_website: true });
      } else saveUnit(u, { website_status: action });
    });
    setSelected(new Set());
  };

  const handleExport = () => {
    const data = onWeb.map(u => ({
      name: getName(u), year: u.year || '', make: u.make || '', model: u.model || '',
      vin: u.chassis || '', mileage: u.mileage || '', website_status: u.website_status || 'live',
      display: u.show_on_website ? 'ON' : 'OFF', featured: u.is_featured ? 'Yes' : 'No',
      photos: (u.images || []).length, has_auction_sheet: (u.auction_sheets || []).length > 0 ? 'Yes' : 'No',
      sku: u.sku || '', updated: u.website_updated_at || '',
    }));
    exportCSV(data, `website-cars-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const toggleFeatured = (unit) => {
    if (!unit.is_featured) {
      const currentFeatured = onWeb.filter(u => u.is_featured).length;
      if (currentFeatured >= MAX_FEATURED) { alert(`Maximum ${MAX_FEATURED} featured units. Remove one first.`); return; }
    }
    saveUnit(unit, { is_featured: !unit.is_featured, ...(!unit.is_featured ? { show_on_website: true } : {}) });
  };

  const getDestination = (unit) => {
    if (unit.assignedTo) {
      const cust = customers.find(c => c.id === unit.assignedTo);
      return cust?.country || cust?.city || '';
    }
    return unit.website_destination || '';
  };

  /* ══════ EDIT MODAL ══════ */
  const EditModal = () => {
    if (!editUnit) return null;
    const [ws, setWs] = useState(editUnit.website_status || 'live');
    const [display, setDisplay] = useState(editUnit.show_on_website !== false);
    const [feat, setFeat] = useState(editUnit.is_featured || false);
    const [order, setOrder] = useState(editUnit.website_sort_order || '');
    const [desc, setDesc] = useState(editUnit.website_description || '');
    const [dest, setDest] = useState(editUnit.website_destination || '');
    const [vessel, setVessel] = useState(editUnit.website_vessel || '');
    const [priceMode, setPriceMode] = useState(editUnit.website_price_mode || 'hidden');
    const [customPrice, setCustomPrice] = useState(editUnit.website_custom_price || '');
    const [priceCurrency, setPriceCurrency] = useState(editUnit.website_price_currency || 'USD');

    const handleSave = () => {
      if (feat && !editUnit.is_featured) {
        const currentFeatured = onWeb.filter(u => u.is_featured && u.id !== editUnit.id).length;
        if (currentFeatured >= MAX_FEATURED) { alert(`Maximum ${MAX_FEATURED} featured units.`); return; }
      }
      saveUnit(editUnit, {
        website_status: ws, show_on_website: display, is_featured: feat,
        website_sort_order: order ? parseInt(order) : null, website_description: desc,
        website_destination: dest, website_vessel: vessel,
        website_price_mode: priceMode, website_custom_price: customPrice ? parseFloat(customPrice) : null,
        website_price_currency: priceCurrency,
      });
      setEditUnit(null);
    };

    return (
      <div className="modal-overlay" onClick={() => setEditUnit(null)}>
        <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
          <div className="modal-header">
            <h3 className="modal-title">Edit Website Listing</h3>
            <button className="modal-close" onClick={() => setEditUnit(null)}>×</button>
          </div>
          <div className="modal-body">
            <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
              <strong>{getName(editUnit)}</strong> — {editUnit.year || ''}<br />
              <span style={{ fontSize: 11, color: 'var(--text3)' }}>{editUnit.sku}</span>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Website Status</label>
                <select className="form-input" value={ws} onChange={e => setWs(e.target.value)}>
                  {WS_STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Display Order</label>
                <input className="form-input" type="number" value={order} onChange={e => setOrder(e.target.value)} placeholder="Lower = first" />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 20, marginBottom: 14 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer' }}>
                <input type="checkbox" checked={display} onChange={e => setDisplay(e.target.checked)} style={{ accentColor: 'var(--green)' }} /> Display on website
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer' }}>
                <input type="checkbox" checked={feat} onChange={e => setFeat(e.target.checked)} style={{ accentColor: '#F59E0B' }} /> ★ Featured
              </label>
            </div>

            <div className="form-group" style={{ marginBottom: 14 }}>
              <label className="form-label">Website Description</label>
              <textarea className="form-input" rows={4} value={desc} onChange={e => setDesc(e.target.value)} placeholder="Custom description for website listing..." />
            </div>

            <div className="form-group" style={{ marginBottom: 14 }}>
              <label className="form-label">Website Price Display</label>
              <select className="form-input" value={priceMode} onChange={e => setPriceMode(e.target.value)}>
                <option value="hidden">Don't show price (customer must inquire)</option>
                <option value="fob">Show FOB price</option>
                <option value="custom">Show custom price</option>
              </select>
              {priceMode === 'custom' && (
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <input className="form-input" type="number" value={customPrice} onChange={e => setCustomPrice(e.target.value)} placeholder="Amount" style={{ flex: 1 }} />
                  <select className="form-input" value={priceCurrency} onChange={e => setPriceCurrency(e.target.value)} style={{ width: 80 }}>
                    <option>USD</option><option>AED</option><option>JPY</option><option>EUR</option>
                  </select>
                </div>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Destination</label>
                <input className="form-input" value={dest} onChange={e => setDest(e.target.value)} placeholder="Kazakhstan" />
              </div>
              <div className="form-group">
                <label className="form-label">Vessel</label>
                <input className="form-input" value={vessel} onChange={e => setVessel(e.target.value)} placeholder="MV Glovis Caravel" />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={() => setEditUnit(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave}>Save Changes</button>
          </div>
        </div>
      </div>
    );
  };

  /* ══════ IMPORT MODAL ══════ */
  const ImportModal = () => {
    const [iSearch, setISearch] = useState('');
    const [iSelected, setISelected] = useState(new Set());
    const [iType, setIType] = useState('');
    const [iYard, setIYard] = useState('');
    const [initStatus, setInitStatus] = useState('live');

    const available = allUnits.filter(u => !u.show_on_website);
    let importable = available;
    if (iType) importable = importable.filter(u => u._type === iType);
    if (iYard) importable = importable.filter(u => (u.yard_location || '') === iYard);
    if (iSearch) {
      const q = iSearch.toLowerCase();
      importable = importable.filter(u => [u.make, u.model, u.chassis, u.sku, u.engineName, u.partName].filter(Boolean).join(' ').toLowerCase().includes(q));
    }

    const handleImport = () => {
      importable.filter(u => iSelected.has(u.id)).forEach(u => {
        saveUnit(u, { show_on_website: true, website_status: initStatus });
      });
      setShowImport(false);
    };

    const toggleAll = () => {
      if (iSelected.size === importable.slice(0, 50).length) setISelected(new Set());
      else setISelected(new Set(importable.slice(0, 50).map(u => u.id)));
    };

    return (
      <div className="modal-overlay" onClick={() => setShowImport(false)}>
        <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 700, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
          <div className="modal-header">
            <h3 className="modal-title">Import Units to Website</h3>
            <button className="modal-close" onClick={() => setShowImport(false)}>×</button>
          </div>
          <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input value={iSearch} onChange={e => setISearch(e.target.value)} placeholder="Search make, model, chassis..." className="form-input" style={{ flex: 1, minWidth: 160, fontSize: 12 }} />
            <select value={iType} onChange={e => setIType(e.target.value)} className="form-input" style={{ width: 120, fontSize: 11 }}>
              <option value="">All Types</option>
              <option value="vehicle">Vehicles</option>
              <option value="engine">Engines</option>
              <option value="part">Parts</option>
            </select>
            <select value={iYard} onChange={e => setIYard(e.target.value)} className="form-input" style={{ width: 120, fontSize: 11 }}>
              <option value="">All Yards</option>
              <option value="japan_yard">Japan</option>
              <option value="sharjah_yard">Sharjah</option>
            </select>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '4px 20px' }}>
            {importable.slice(0, 50).map(u => {
              const icon = u._type === 'vehicle' ? '🚗' : u._type === 'engine' ? '⚙️' : '🔧';
              const photos = (u.images || []).length;
              return (
                <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--border)', cursor: 'pointer', fontSize: 12 }}>
                  <input type="checkbox" checked={iSelected.has(u.id)} onChange={() => { const n = new Set(iSelected); if (n.has(u.id)) n.delete(u.id); else n.add(u.id); setISelected(n); }}
                    style={{ width: 15, height: 15, accentColor: 'var(--blue)' }} />
                  <div style={{ width: 40, height: 30, borderRadius: 4, background: 'var(--bg3)', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {u.images?.[0] ? <img src={u.images[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 14 }}>{icon}</span>}
                  </div>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontWeight: 600, color: 'var(--text1)' }}>{icon} {getName(u)}</span>
                    {u.year && <span style={{ color: 'var(--text3)' }}> · {u.year}</span>}
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>
                      {u.sku} · {(u.yard_location || 'japan_yard').replace('_', ' ')} · {u.status || 'available'} · {photos} photos
                    </div>
                  </div>
                </label>
              );
            })}
            {importable.length === 0 && <div style={{ padding: 32, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>All units are already on the website.</div>}
            {importable.length > 50 && <div style={{ padding: 12, textAlign: 'center', fontSize: 10, color: 'var(--text3)' }}>Showing 50 of {importable.length}. Use search to narrow down.</div>}
          </div>
          <div style={{ position: 'sticky', bottom: 0, padding: '14px 20px', background: 'var(--bg1)', borderTop: '2px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, boxShadow: '0 -4px 12px rgba(0,0,0,0.15)', zIndex: 10, borderRadius: '0 0 12px 12px' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button className="btn btn-secondary btn-sm" onClick={toggleAll} style={{ fontSize: 10 }}>Select All</button>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text1)', padding: '6px 12px', background: 'var(--bg3)', borderRadius: 6, border: '1px solid var(--border)' }}>Selected: {iSelected.size}</span>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: 'var(--text3)' }}>Initial status:</span>
              <select value={initStatus} onChange={e => setInitStatus(e.target.value)} className="form-input" style={{ width: 130, fontSize: 11 }}>
                {WS_STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
              <button className="btn btn-primary" disabled={iSelected.size === 0} onClick={handleImport} style={{ padding: '8px 20px', fontSize: 13, fontWeight: 600 }}>
                Import {iSelected.size} Unit{iSelected.size !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /* ══════ IMAGE PREVIEW MODAL ══════ */
  const ImgPreview = () => {
    if (!imgPreview) return null;
    return (
      <div className="modal-overlay" onClick={() => setImgPreview(null)}>
        <div onClick={e => e.stopPropagation()} style={{ maxWidth: '90vw', maxHeight: '85vh' }}>
          <img src={imgPreview} alt="" style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: 8 }} />
        </div>
      </div>
    );
  };

  /* ══════ MAIN RENDER ══════ */
  return (
    <div onClick={() => setMenuOpen(null)}>
      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Inventory</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text1)', marginBottom: 4 }}>Cars on website</h1>
            <p style={{ fontSize: 12, color: 'var(--text3)' }}>Cars are imported from your private portal. Toggle visibility, change status, or edit per-car copy.</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary btn-sm" onClick={handleExport}>↓ Export</button>
            <button className="btn btn-primary btn-sm" onClick={() => setShowImport(true)}>
              + Import from portal{importableCount > 0 && ` (${importableCount} available)`}
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: 14 }}>
        {[
          { id: 'all', label: 'All' }, { id: 'live', label: 'Live' }, { id: 'reserved', label: 'Reserved' },
          { id: 'arriving', label: 'Arriving' }, { id: 'sold', label: 'Sold' }, { id: 'hidden', label: 'Hidden' },
        ].map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); setPage(1); setSelected(new Set()); }} className={`tab ${tab === t.id ? 'active' : ''}`}>
            {t.label}<span className="cnt">{counts[t.id]}</span>
          </button>
        ))}
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', background: 'var(--bg2)', borderRadius: 8, border: '1px solid var(--border)', marginBottom: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text1)' }}>☑ {selected.size} selected</span>
          <div style={{ width: 1, height: 18, background: 'var(--border)' }} />
          <button className="btn btn-sm" style={{ background: '#22C55E', color: '#fff', border: 'none', fontSize: 10, padding: '4px 10px' }} onClick={() => bulkAction('live')}>Set Live</button>
          <button className="btn btn-sm" style={{ background: '#F59E0B', color: '#fff', border: 'none', fontSize: 10, padding: '4px 10px' }} onClick={() => bulkAction('reserved')}>Set Reserved</button>
          <button className="btn btn-sm" style={{ background: '#3B82F6', color: '#fff', border: 'none', fontSize: 10, padding: '4px 10px' }} onClick={() => bulkAction('arriving')}>Set Arriving</button>
          <button className="btn btn-sm" style={{ background: '#6B7280', color: '#fff', border: 'none', fontSize: 10, padding: '4px 10px' }} onClick={() => bulkAction('sold')}>Set Sold</button>
          <button className="btn btn-sm" style={{ background: '#F59E0B', color: '#fff', border: 'none', fontSize: 10, padding: '4px 10px' }} onClick={() => bulkAction('feature')}>⭐ Feature</button>
          <button className="btn btn-secondary btn-sm" style={{ fontSize: 10, padding: '4px 10px' }} onClick={() => bulkAction('hide')}>Hide</button>
          <button className="btn btn-secondary btn-sm" style={{ fontSize: 10, marginLeft: 'auto' }} onClick={() => setSelected(new Set())}>Clear</button>
        </div>
      )}

      {/* Search */}
      <div style={{ marginBottom: 14, display: 'flex', gap: 8, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)' }}><SearchIcon /></span>
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search VIN, make, model..." className="form-input" style={{ paddingLeft: 32, fontSize: 12 }} />
        </div>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="empty" style={{ padding: '48px 20px' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📋</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>
            {tab === 'all' ? 'No units on website yet' : `No ${tab} units`}
          </div>
          <button className="btn btn-primary btn-sm" style={{ marginTop: 10 }} onClick={() => setShowImport(true)}>+ Import from portal</button>
        </div>
      ) : (<>
        {/* Pagination (top) */}
        <Pagination total={filtered.length} page={safePage} perPage={perPage}
          onPageChange={setPage} onPerPageChange={n => { setPage(1); /* perPage is const 15 but allow override */ }} />
        <div className="card" style={{ overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={{ width: 32, padding: '8px 6px' }}>
                    <input type="checkbox" checked={selected.size === paged.length && paged.length > 0} onChange={selectAllPage} style={{ width: 14, height: 14, accentColor: 'var(--blue)', cursor: 'pointer' }} />
                  </th>
                  {['Photo', 'Vehicle', 'VIN / Mileage', 'Status', '⭐', 'Display', 'Destination', 'Updated', ''].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 8px', fontSize: 9, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paged.map(u => {
                  const name = getName(u);
                  const photos = (u.images || []).length;
                  const hasSheets = (u.auction_sheets || []).length > 0;
                  const specs = getSpecs(u);
                  const km = u.mileage ? Number(u.mileage).toLocaleString() + ' km' : 'New · 0 km';
                  const chassis = u.chassis || '—';
                  const ws = u.website_status || 'live';
                  const wsSt = WS_STATUSES.find(s => s.id === ws) || WS_STATUSES[0];
                  const isSold = ['sold', 'sold_awaiting_payment', 'shipped', 'delivered'].includes(u.status);
                  const dest = getDestination(u);

                  return (
                    <tr key={u.id} style={{ borderBottom: '1px solid var(--border)' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg2)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = ''; }}>
                      {/* Checkbox */}
                      <td style={{ padding: '8px 6px' }}>
                        <input type="checkbox" checked={selected.has(u.id)} onChange={() => toggleSelect(u.id)} style={{ width: 14, height: 14, accentColor: 'var(--blue)', cursor: 'pointer' }} />
                      </td>
                      {/* Photo */}
                      <td style={{ padding: '8px 6px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div onClick={() => u.images?.[0] && setImgPreview(u.images[0])} style={{ width: 44, height: 33, borderRadius: 4, background: 'var(--bg3)', overflow: 'hidden', cursor: u.images?.[0] ? 'pointer' : 'default', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {u.images?.[0] ? <img src={u.images[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 10, color: 'var(--text3)' }}>no img</span>}
                          </div>
                          <div>
                            <span style={{ display: 'inline-block', padding: '1px 5px', borderRadius: 4, background: photos === 0 ? 'rgba(239,68,68,0.1)' : 'var(--bg3)', color: photos === 0 ? '#EF4444' : 'var(--text2)', fontSize: 10, fontWeight: 600 }}>
                              {photos}p{photos === 0 && '⚠️'}
                            </span>
                            {hasSheets && <span style={{ fontSize: 10, marginLeft: 2 }} title="Has auction sheets">📄</span>}
                          </div>
                        </div>
                      </td>
                      {/* Vehicle */}
                      <td style={{ padding: '8px 8px', maxWidth: 200 }}>
                        <div style={{ fontWeight: 600, color: 'var(--text1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 190, cursor: 'pointer' }} onClick={() => setEditUnit(u)}>{name}</div>
                        <div style={{ fontSize: 10, color: 'var(--text3)' }}>{specs}</div>
                      </td>
                      {/* VIN / Mileage */}
                      <td style={{ padding: '8px 8px' }}>
                        <div style={{ fontFamily: 'monospace', fontSize: 10, color: 'var(--text2)' }}>{chassis}</div>
                        <div style={{ fontSize: 10, color: 'var(--text3)' }}>{km}</div>
                      </td>
                      {/* Status dropdown */}
                      <td style={{ padding: '8px 8px' }}>
                        <select value={ws} onChange={e => saveUnit(u, { website_status: e.target.value })}
                          style={{ padding: '3px 6px', borderRadius: 5, border: '1px solid var(--border)', background: 'var(--bg2)', color: wsSt.color, fontSize: 10, fontWeight: 600, cursor: 'pointer', outline: 'none', maxWidth: 120 }}>
                          {WS_STATUSES.map(s => <option key={s.id} value={s.id}>{s.dot} {s.label}</option>)}
                        </select>
                        {isSold && ws !== 'sold' && <span style={{ fontSize: 9, color: 'var(--text3)', display: 'block', marginTop: 2 }}>🔒 Internal: sold</span>}
                      </td>
                      {/* Featured */}
                      <td style={{ padding: '8px 6px', textAlign: 'center' }}>
                        <button onClick={() => toggleFeatured(u)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, opacity: u.is_featured ? 1 : 0.2 }} title={u.is_featured ? 'Remove featured' : 'Set featured'}>⭐</button>
                      </td>
                      {/* Display toggle */}
                      <td style={{ padding: '8px 6px' }}>
                        <div onClick={() => saveUnit(u, { show_on_website: !u.show_on_website, ...(!u.show_on_website ? {} : { is_featured: false }) })}
                          style={{ width: 38, height: 20, borderRadius: 10, background: u.show_on_website ? '#22C55E' : 'var(--bg3)', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', border: '1px solid var(--border)' }}>
                          <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: u.show_on_website ? 21 : 2, transition: 'left 0.2s', boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }} />
                        </div>
                      </td>
                      {/* Destination */}
                      <td style={{ padding: '8px 8px', maxWidth: 120 }}>
                        <div style={{ fontSize: 11, color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dest || '—'}</div>
                        <div style={{ fontSize: 9, color: 'var(--text3)' }}>{u.website_vessel || ''}</div>
                      </td>
                      {/* Updated */}
                      <td style={{ padding: '8px 8px' }}>
                        <div style={{ fontSize: 10, color: 'var(--text3)' }}>{timeAgo(u.website_updated_at || u.updatedAt || u.createdAt)}</div>
                        <div style={{ fontSize: 9, color: 'var(--text3)' }}>{u.website_updated_by || 'Yard / Auto'}</div>
                      </td>
                      {/* Actions */}
                      <td style={{ padding: '8px 6px' }}>
                        <div style={{ display: 'flex', gap: 3 }}>
                          <button onClick={() => setEditUnit(u)} style={{ width: 26, height: 26, borderRadius: 5, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text3)', fontSize: 11, cursor: 'pointer', display: 'grid', placeItems: 'center' }} title="Edit listing"><EditIcon /></button>
                          <div style={{ position: 'relative' }}>
                            <button onClick={e => { e.stopPropagation(); setMenuOpen(menuOpen === u.id ? null : u.id); }} style={{ width: 26, height: 26, borderRadius: 5, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text3)', fontSize: 12, cursor: 'pointer', display: 'grid', placeItems: 'center' }}>⋮</button>
                            {menuOpen === u.id && (
                              <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, background: 'var(--bg1)', border: '1px solid var(--border)', borderRadius: 8, padding: 3, zIndex: 50, minWidth: 160, boxShadow: '0 6px 20px rgba(0,0,0,0.35)' }}>
                                <MBtn onClick={() => { window.open('/', '_blank'); setMenuOpen(null); }}>↗ View on website</MBtn>
                                <MBtn onClick={() => { setEditUnit(u); setMenuOpen(null); }}>✏️ Edit listing</MBtn>
                                <MBtn onClick={() => { saveUnit(u, { show_on_website: false, website_status: 'hidden', is_featured: false }); setMenuOpen(null); }}>🚫 Remove from website</MBtn>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        {/* Pagination (bottom) */}
        <Pagination total={filtered.length} page={safePage} perPage={perPage}
          onPageChange={setPage} onPerPageChange={n => { setPage(1); }} />
      </>)}

      {/* Modals */}
      {editUnit && <EditModal />}
      {showImport && <ImportModal />}
      {imgPreview && <ImgPreview />}
    </div>
  );
}

function MBtn({ onClick, children }) {
  return (
    <button onClick={onClick} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 12px', fontSize: 11, color: 'var(--text2)', background: 'none', border: 'none', cursor: 'pointer', borderRadius: 5 }}
      onMouseEnter={e => { e.target.style.background = 'var(--bg2)'; }}
      onMouseLeave={e => { e.target.style.background = 'none'; }}>
      {children}
    </button>
  );
}

export default WebsiteCars;
